import React, { FC } from 'react'
import {
  Box,
  Text,
  Heading,
  HStack,
  Button,
  VStack,
  Skeleton,
  LayoutProps,
} from '@chakra-ui/react'
import { useColor } from '../../theme'
import { ICommonProps } from './types'

interface ICardProps extends ICommonProps {
  bannerWidth: LayoutProps['w']
  itemsPerRow: number
}

export const Card: FC<ICardProps> = ({
  actionCaption,
  actionIcon,
  actionText,
  showAction,
  actionDisabled,
  bannerWidth = '100%',
  caption,
  handleAction,
  itemsPerRow,
  title,
}) => {
  const captionColor = useColor({ nuance: 3, palette: 'text' })
  const titleColor = useColor({ nuance: 6, palette: 'text' })
  const whiteColor = useColor({ nuance: 'white', palette: 'colors' })
  const actionCaptionColor = useColor({ nuance: 7, palette: 'text' })

  const IconComponent = actionIcon

  // In order to calculate the width of the Card we need to know the available width
  // from the Banner, the amount of items there is in the visual row bellow to match
  // the same width (as it is set as auto there is no fixed value we can rely on) and
  // the spacing between items (DEFAULT_TILE_SPACING: 8 -> 2rem) which we convert into `rem`
  // according to the refecenre from chakra-ui.
  // Reference: https://chakra-ui.com/docs/styled-system/theme
  const TILE_SPACING_TO_REM = '2rem'

  return (
    <VStack
      alignItems="flex-start"
      backgroundColor={whiteColor}
      borderRadius="2xl"
      boxShadow="md"
      padding={4}
      width={`calc(${bannerWidth} / ${itemsPerRow} - ${TILE_SPACING_TO_REM})`}>
      <Box width="full">
        <Text
          color={captionColor}
          data-testid="card-caption"
          fontSize="sm"
          fontWeight={700}
          noOfLines={1}>
          {caption}
        </Text>

        <Heading
          color={titleColor}
          data-testid="card-heading"
          fontSize="2xl"
          width="full">
          {title}
        </Heading>
      </Box>
      {showAction ? (
        <HStack height="full" paddingTop={2} width="full">
          <Button
            data-testid="card-action-button"
            flexShrink={0}
            iconSpacing={2}
            isDisabled={actionDisabled}
            leftIcon={<IconComponent boxSize={4} />}
            minWidth="6rem"
            onClick={(e) => {
              e.stopPropagation()
              handleAction()
            }}
            px={2}>
            {actionText}
          </Button>

          <Text
            color={actionCaptionColor}
            data-testid="action-caption"
            fontSize="sm"
            overflow="hidden"
            paddingLeft={2}
            textOverflow="ellipsis"
            whiteSpace="pre">
            {actionCaption}
          </Text>
        </HStack>
      ) : (
        <HStack height="full" marginTop={4} maxWidth="90%">
          <Skeleton height={8} width={32} />
          <Skeleton height={4} width={24} />
        </HStack>
      )}
    </VStack>
  )
}
